import {
  AuthAccountCanadaVisaStatusChoices,
  AuthAccountEducationDegreeChoices,
  AuthAccountProfileGenderChoices,
  AuthAccountProfileJobTypeExcludeChoices,
  AuthAccountProfileNativeLanguageChoices,
  AuthAccountWorkExperienceGradeChoices,
  getLanguages,
  InputConfig,
} from "@cpj-ai/panel-npm-package";
import {
  AuthAccountUserFluentLanguagesChoices,
  AuthAccountUserJobTypeChoices,
  AuthAccountUserOrderByChoices,
  useGetCitiesLazyQuery,
  useGetCountriesLazyQuery,
  useGetIndustriesLazyQuery,
  useGetJobPositionsSearchLazyQuery,
  useGetJobsLazyQuery,
  useGetSkillsLazyQuery,
} from "@graphql/generated";

type ObjectType = {
  node: {
    id: string;
    title: string;
  };
};

export interface IFormDataNormalized {
  fullName?: string;
  gender?: AuthAccountProfileGenderChoices[];
  birthDate?: Date[];
  email?: string;
  phoneNumber?: string;
  country?: ObjectType[];
  city?: ObjectType[];
  interestedJobs?: ObjectType[];
  industry?: ObjectType[];
  visaStatus?: AuthAccountCanadaVisaStatusChoices[];
  skills?: ObjectType[];
  nativeLanguage?: AuthAccountProfileNativeLanguageChoices[];
  educationDegree?: AuthAccountEducationDegreeChoices[];
  workexperiencesGrade?: AuthAccountWorkExperienceGradeChoices[];
  jobType?: AuthAccountUserJobTypeChoices[];
  jobCitiesId?: ObjectType[];
  fluentLanguages: AuthAccountUserFluentLanguagesChoices[];
  referredJobPositionsId?: ObjectType[];
  isAssessmentsDone?: boolean;
  orderBy?: AuthAccountUserOrderByChoices;
}

export interface IFormData {
  fullName?: string;
  gender?: { value: AuthAccountProfileGenderChoices; label: string }[];
  birthDate?: Date[];
  email?: string;
  phoneNumber?: string;
  country?: ObjectType[];
  city?: ObjectType[];
  interestedJobs?: ObjectType[];
  industry?: ObjectType[];
  visaStatus?: { value: AuthAccountCanadaVisaStatusChoices; label: string }[];
  skills?: ObjectType[];
  nativeLanguage?: { value: AuthAccountProfileNativeLanguageChoices; label: string }[];
  educationDegree?: { value: AuthAccountEducationDegreeChoices; label: string }[];
  workexperiencesGrade?: { value: AuthAccountWorkExperienceGradeChoices; label: string }[];
  jobType?: { value: AuthAccountUserJobTypeChoices; label: string }[];
  jobCitiesId?: ObjectType[];
  fluentLanguages?: { value: AuthAccountUserFluentLanguagesChoices; label: string }[];
  referredJobPositionsId?: ObjectType[];
  isAssessmentsDone?: { value: boolean; label: string };
  orderBy?: { value: AuthAccountUserOrderByChoices; label: string };
}

export const getInputConfigs = (): InputConfig[] => [
  {
    name: "orderBy",
    previewSize: 12,
    inputProps: {
      type: "searcher",
      label: "Order By:",
      placeholder: "Select order by",
      labelKey: "label",
      valueKey: "value",
      items: [
        { label: "Date Joined (ascending)", value: AuthAccountUserOrderByChoices.DATE_JOINED },
        { label: "Date Joined (descending)", value: AuthAccountUserOrderByChoices._DATE_JOINED },
      ],
    },
  },
  {
    name: "fullName",
    previewSize: 12,
    inputProps: {
      type: "text",
      label: "Full Name:",
      placeholder: "Enter full name",
    },
  },
  {
    name: "gender",
    previewSize: 12,
    inputProps: {
      type: "searcher",
      label: "Gender:",
      placeholder: "Select gender",
      labelKey: "label",
      valueKey: "value",
      isMulti: true,
      items: [
        { label: "Male", value: AuthAccountProfileGenderChoices.MALE },
        { label: "Female", value: AuthAccountProfileGenderChoices.FEMALE },
        { label: "Not Applicable", value: AuthAccountProfileGenderChoices.NOT_APPLICABLE },
        { label: "Not Known", value: AuthAccountProfileGenderChoices.NOT_KNOWN },
      ],
    },
  },
  {
    name: "birthDate",
    previewSize: 12,
    inputProps: {
      type: "date",
      label: "Birth Date Range:",
      placeholder: "Select date range",
      range: true,
    },
  },
  {
    name: "email",
    previewSize: 12,

    inputProps: {
      type: "text",
      label: "Email:",
      placeholder: "Enter email",
    },
  },
  {
    name: "phoneNumber",
    previewSize: 12,

    inputProps: {
      type: "text",
      label: "Phone:",
      placeholder: "Enter phone",
    },
  },
  {
    name: "country",
    previewSize: 12,

    inputProps: {
      type: "searcher",
      label: "Country:",
      dataPath: "common.countries",
      useLazyQuery: useGetCountriesLazyQuery,
      isMulti: true,
    },
  },
  {
    name: "city",
    previewSize: 12,

    inputProps: {
      type: "searcher",
      label: "City:",
      useLazyQuery: useGetCitiesLazyQuery,
      dataPath: "common.cities",
      isMulti: true,
    },
  },

  {
    name: "industry",
    previewSize: 12,
    inputProps: {
      type: "searcher",
      label: "Industries:",
      placeholder: "Select industries",
      useLazyQuery: useGetIndustriesLazyQuery,
      dataPath: "common.industries",
      labelKey: "title",
      valueKey: "id",
      isMulti: true,
    },
  },
  {
    name: "visaStatus",
    previewSize: 12,
    inputProps: {
      type: "searcher",
      label: "Visa Status in Canada:",
      placeholder: "Select your visa status",
      labelKey: "label",
      valueKey: "value",
      isMulti: true,
      items: [
        { label: "Citizenship", value: AuthAccountCanadaVisaStatusChoices.CITIZENSHIP },
        {
          label: "Electronic Travel Authorization (eTA)",
          value: AuthAccountCanadaVisaStatusChoices.ELECTRONIC_TRAVEL_AUTHORIZATION,
        },
        {
          label: "International Experience Canada (IEC)",
          value: AuthAccountCanadaVisaStatusChoices.INTERNATIONAL_EXPERIENCE_CANADA,
        },
        { label: "Permanent Resident (PR)", value: AuthAccountCanadaVisaStatusChoices.PERMANENT_RESIDENT },
        { label: "Refugee (Work Permit)", value: AuthAccountCanadaVisaStatusChoices.REFUGEE_WORK_PERMIT },
        {
          label: "Seasonal Agricultural Worker Program (SAWP)",
          value: AuthAccountCanadaVisaStatusChoices.SEASONAL_AGRICULTURAL_WORKER_PROGRAM,
        },
        {
          label: "Temporary Resident (Close Work Permit)",
          value: AuthAccountCanadaVisaStatusChoices.TEMPORARY_RESIDENT_CLOSE_WORK_PERMIT,
        },
        {
          label: "Temporary Resident (Open Work Permit)",
          value: AuthAccountCanadaVisaStatusChoices.TEMPORARY_RESIDENT_OPEN_WORK_PERMIT,
        },
        {
          label: "Temporary Resident (Study Permit)",
          value: AuthAccountCanadaVisaStatusChoices.TEMPORARY_RESIDENT_STUDY_PERMIT,
        },
        { label: "Visitor Visa", value: AuthAccountCanadaVisaStatusChoices.VISITOR_VISA },
      ],
    },
  },
  {
    name: "skills",
    previewSize: 12,
    inputProps: {
      type: "searcher",
      label: "Skills:",
      placeholder: "Select skills",
      useLazyQuery: useGetSkillsLazyQuery,
      dataPath: "common.skills",
      labelKey: "title",
      searchKey: "title_Icontains",
      valueKey: "id",
      isMulti: true,
    },
  },
  {
    name: "nativeLanguage",
    previewSize: 12,
    inputProps: {
      type: "searcher",
      label: "Native language:",
      placeholder: "Native Language",
      items: getLanguages(),
      labelKey: "label",
      valueKey: "value",
      isMulti: true,
    },
  },
  {
    name: "fluentLanguages",
    previewSize: 12,
    inputProps: {
      type: "searcher",
      label: "Fluent languages:",
      placeholder: "Fluent languages",
      items: getLanguages(),
      labelKey: "label",
      valueKey: "value",
      isMulti: true,
    },
  },
  {
    name: "educationDegree",
    previewSize: 12,
    inputProps: {
      type: "searcher",
      label: "Education Degree:",
      placeholder: "Education Degree",
      labelKey: "label",
      valueKey: "value",
      isMulti: true,
      items: [
        { label: "Certificate", value: AuthAccountEducationDegreeChoices.CERTIFICATE },
        { label: "Diploma", value: AuthAccountEducationDegreeChoices.DIPLOMA },
        { label: "Associate", value: AuthAccountEducationDegreeChoices.ASSOCIATE },
        { label: "Bachelors", value: AuthAccountEducationDegreeChoices.BACHELORS },
        { label: "Masters", value: AuthAccountEducationDegreeChoices.MASTERS },
        { label: "PhD", value: AuthAccountEducationDegreeChoices.PHD },
      ],
    },
  },
  {
    name: "workexperiencesGrade",
    previewSize: 12,
    inputProps: {
      type: "searcher",
      label: "Work Experience Grade:",
      placeholder: "Work Experience Grade",
      labelKey: "label",
      valueKey: "value",
      isMulti: true,
      items: [
        { label: "Intern", value: AuthAccountWorkExperienceGradeChoices.INTERN },
        { label: "Associate", value: AuthAccountWorkExperienceGradeChoices.ASSOCIATE },
        { label: "Mid-Level", value: AuthAccountWorkExperienceGradeChoices.MID_LEVEL },
        { label: "Senior", value: AuthAccountWorkExperienceGradeChoices.SENIOR },
        { label: "Manager", value: AuthAccountWorkExperienceGradeChoices.MANAGER },
        { label: "Director", value: AuthAccountWorkExperienceGradeChoices.DIRECTOR },
        { label: "CTO", value: AuthAccountWorkExperienceGradeChoices.CTO },
        { label: "CFO", value: AuthAccountWorkExperienceGradeChoices.CFO },
        { label: "CEO", value: AuthAccountWorkExperienceGradeChoices.CEO },
      ],
    },
  },
  {
    name: "interestedJobs",
    previewSize: 12,
    inputProps: {
      type: "searcher",
      label: "Interested fields of work:",
      placeholder: "Interested fields of work",
      useLazyQuery: useGetJobsLazyQuery,
      dataPath: "common.jobs",
      labelKey: "title",
      searchKey: "title_Icontains",
      valueKey: "id",
      isMulti: true,
    },
  },
  {
    name: "jobCitiesId",
    previewSize: 12,
    inputProps: {
      type: "searcher",
      label: "Interested Job Cities:",
      useLazyQuery: useGetCitiesLazyQuery,
      dataPath: "common.cities",
      isMulti: true,
      additionalFilters: {
        country: "CA",
      },
    },
  },
  {
    name: "jobType",
    previewSize: 12,
    inputProps: {
      type: "searcher",
      label: "Interested Job Types:",
      isMulti: true,
      placeholder: "Select job type",
      labelKey: "label",
      valueKey: "value",
      items: [
        { label: "Full-time", value: AuthAccountProfileJobTypeExcludeChoices.FULL_TIME },
        { label: "Part-time", value: AuthAccountProfileJobTypeExcludeChoices.PART_TIME },
        { label: "Permanent", value: AuthAccountProfileJobTypeExcludeChoices.PERMANENT },
        { label: "Fix term contract", value: AuthAccountProfileJobTypeExcludeChoices.FIX_TERM_CONTRACT },
        { label: "Seasonal", value: AuthAccountProfileJobTypeExcludeChoices.SEASONAL },
        { label: "Freelance", value: AuthAccountProfileJobTypeExcludeChoices.FREELANCE },
        { label: "Apprenticeship", value: AuthAccountProfileJobTypeExcludeChoices.APPRENTICESHIP },
        { label: "Prince Edward Island", value: AuthAccountProfileJobTypeExcludeChoices.PRINCE_EDWARD_ISLAND },
        { label: "Internship/Co-op", value: AuthAccountProfileJobTypeExcludeChoices.INTERNSHIP_CO_OP },
      ],
    },
  },
  {
    name: "referredJobPositionsId",
    previewSize: 12,
    inputProps: {
      type: "searcher",
      label: "Referred Job Positions:",
      useLazyQuery: useGetJobPositionsSearchLazyQuery,
      dataPath: "admin.jobPositions",
      labelKey: "title",
      searchKey: "title_Icontains",
      valueKey: "id",
      isMulti: true,
    },
  },
  {
    name: "isAssessmentsDone",
    previewSize: 12,
    inputProps: {
      type: "searcher",
      label: "Is Assessments Done:",
      placeholder: "Is Assessments Done",
      labelKey: "label",
      valueKey: "value",
      items: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
    },
  },
];
